import React, {CSSProperties, forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react'

import {statusColors} from '../styles.js'
import {StatusVariant, toastIcons, toastStyle} from './styles.js'

export interface ToastHandle {
    /** Shows msg for time ms (default 2500); a Toast that is showing closes first */
    showToast: (variant: StatusVariant, msg: string, time?: number) => void
}

interface ToastProps {
    style?: CSSProperties
}

interface ToastState {
    open: boolean
    variant: StatusVariant
    msg: string
}

/**
 * A short message at the bottom centre of the window, with the icon of its variant in the status colour. Shown with
 * the ref: toastRef.current.showToast(variant, msg, time). Screen readers read the message (aria-live).
 */
const Toast = forwardRef<ToastHandle, ToastProps>(function Toast({style}, ref) {
    const [state, setState] = useState<ToastState>({open: false, variant: 'success', msg: ''})
    const hideTimer = useRef<ReturnType<typeof setTimeout>>(null)
    const showTimer = useRef<ReturnType<typeof setTimeout>>(null)
    const open = useRef(false)

    const clearTimers = () => {
        clearTimeout(hideTimer.current)
        clearTimeout(showTimer.current)
    }

    useEffect(() => clearTimers, [])

    useImperativeHandle(ref, () => ({
        showToast(variant, msg, time) {
            clearTimers()

            const show = () => {
                open.current = true
                setState({open: true, variant: statusColors[variant] ? variant : 'success', msg})
                hideTimer.current = setTimeout(() => {
                    open.current = false
                    setState((current) => ({...current, open: false}))
                }, time || toastStyle.duration)
            }

            if (open.current) {
                // Close the one that is showing first, so the new message is seen as new
                open.current = false
                setState((current) => ({...current, open: false}))
                showTimer.current = setTimeout(show, toastStyle.transition)
            } else {
                show()
            }
        }
    }), [])

    const {variant, msg} = state

    const toast: CSSProperties = {
        position: 'fixed',
        display: 'flex',
        alignItems: 'center',
        bottom: toastStyle.bottom,
        left: '50%',
        minWidth: toastStyle.minWidth,
        padding: toastStyle.padding,
        background: toastStyle.background,
        color: toastStyle.text,
        zIndex: toastStyle.zIndex,
        pointerEvents: state.open ? 'auto' : 'none',
        opacity: state.open ? 1 : 0,
        transform: `translateX(-50%) scale(${state.open ? 1 : 0.95})`,
        transition: `all ${toastStyle.transition}ms ease`,
        boxShadow: toastStyle.shadow,
        borderRadius: toastStyle.radius,
        ...style
    }

    const iconStyle: CSSProperties = {
        height: toastStyle.iconSize,
        fontSize: toastStyle.iconSize,
        marginRight: toastStyle.iconGap,
        transform: 'translateY(-1px)',
        color: statusColors[variant].bright
    }

    return (
        <div role='status' aria-live='polite' style={toast}>
            {msg && <i aria-hidden className={toastIcons[variant]} style={iconStyle} />}
            {msg}
        </div>
    )
})

export default Toast
